<template>
  <v-card>
    <section v-if="!fileUploaded">
      <h2 class="text-center">Download your Letterboxd Data</h2>
      <ol class="mb-4">
        <li>
          Click the link
          <a href="https://letterboxd.com/settings/data/" target="_blank">Here</a>
          to go to your Letterboxd Settings
        </li>
        <li>Click "Export your Data"</li>
        <li>
          Unzip the downloaded File
          <ul>
            <li><strong>For Mac:</strong> double click the zipped file</li>
            <li><strong>For Windows:</strong> Right click and select "Extract All"</li>
          </ul>
        </li>
        <li>Upload Your file. Acceptable files include reviews.csv, ratings.csv, and watched.csv</li>
        <li>For all data to copy over, you'll have to repeat the process for all three files</li>
        <li>
          To prevent duplicates, upload your reviews
          <strong>before</strong> your ratings
        </li>
      </ol>

      <h2 class="text-center">Upload your file</h2>
      <div class="d-flex justify-center">
        <div id="fileInput">
          <v-file-input v-model="file" label="csv file" class="mr-3" />
        </div>
        <div id="contentSelect">
          <v-select v-model="contentType" label="file type" :items="['review', 'rating', 'watched']" />
        </div>
        <v-btn id="upload" color="primary" :loading="isUploading" class="mr-2 my-auto" @click="upload"> Upload </v-btn>
      </div>
    </section>

    <section v-else-if="clarifications.length > 0 && entryIdx < clarifications.length">
      <h2 class="text-center">
        Please Choose the Correct Choice For <br />
        <em>{{ clarifications[entryIdx].title }}</em> ({{ clarifications[entryIdx].year }})
      </h2>

      <div class="d-flex justify-center">
        <v-btn v-if="entryIdx > 0" class="mt-3" @click="back"> back </v-btn>
        <v-btn class="mt-3" color="primary" @click="next">
          <!-- Doesn't Update if Item is unselected -->
          <span v-if="entrySelected">Next</span>
          <span v-else>Skip</span>
        </v-btn>
      </div>

      <v-container fluid>
        <v-row no-gutters>
          <v-col v-for="suggestion in clarifications[entryIdx].suggestions" :key="suggestion.id" cols="3" sm="2">
            <v-card
              :id="'poster' + suggestion.id"
              :ref="'poster' + suggestion.id"
              :class="getClasses(suggestion.id)"
              outlined
              @click="confirm(clarifications[entryIdx], suggestion.id)"
            >
              <v-img
                :src="'https://image.tmdb.org/t/p/original' + suggestion.poster_path"
                lazy-src="https://cloud.filmfed.com/defaults/movie-poster/m_movie_poster_default.png"
              />
              <v-card-title>{{ suggestion.title }}</v-card-title>
              <v-card-text v-if="suggestion.release_date">
                {{ suggestion.release_date.slice(0, 4) }}
              </v-card-text>
              <v-btn
                v-if="confirmedReviews[entryIdx] && suggestion.id === confirmedReviews[entryIdx].workID"
                :id="`button${suggestion.id}`"
                small
                text
                color="red"
                width="100%"
                @click.stop="unselect(suggestion.id)"
              >
                Unselect
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <div class="d-flex">
        <h3 id="searchPrompt" class="ml-5 mr-3">Search for Movie:</h3>
        <media-search medium="film" class="media-search" @workSelected="addOption($event)" />
      </div>
    </section>

    <section v-else-if="entryIdx === clarifications.length && entryIdx > 0">
      <h1 class="text-center">No More Questions</h1>
      <div class="d-flex justify-center pa-5">
        <v-btn @click="back"> back </v-btn>
        <v-btn color="primary" @click="submitReviews"> submit </v-btn>
      </div>
    </section>

    <section v-else-if="entryIdx > clarifications.length && uploadSuccessful">
      <h1 class="text-center">Upload Complete!</h1>
      <v-card-actions class="d-flex justify-space-around">
        <v-btn :to="{ name: 'home' }" exact text color="indigo"> Back to Main Feed </v-btn>

        <v-btn text color="indigo" @click="reset"> Import More Data </v-btn>
      </v-card-actions>
    </section>

    <snackbar />
  </v-card>
</template>

<script>
import MediaSearch from "../components/media-search.vue";
import Snackbar from "../components/snackbar.vue";

export default {
  name: "ImportDataPage",

  components: {
    Snackbar,
    MediaSearch,
  },

  data: () => ({
    file: undefined,
    contentType: undefined,
    isUploading: false,
    fileUploaded: false,

    clarifications: [],
    entryIdx: 0,

    confirmedReviews: [],

    uploadSuccessful: true,
  }),

  computed: {
    entrySelected: function () {
      return this.confirmedReviews[this.entryIdx] !== undefined;
    },
  },

  methods: {
    upload: async function () {
      let formData = new FormData();
      formData.append("file", this.file);
      formData.append("content", this.contentType);
      this.isUploading = true;

      try {
        let response = await this.$axios.post("/letterboxd-import", formData, {
          headers: {
            "Content-Type": `multipart/form-data`,
          },
        });
        this.fileUploaded = true;
        this.clarifications = response.data;
      } catch (e) {
        // TODO: differentiate between invalid file type and other errors
        this.$store.commit("showSnackbar", {
          text: "An error has occurred",
          error: true,
        });
      }

      this.isUploading = false;
    },

    confirm: async function (review, id) {
      this.confirmedReviews[this.entryIdx] = {
        workID: id,
        medium: this.$mediums.FILM,
        rating: review.rating,
        review: review.review,
        date_viewed: review.dateViewed,
        time_posted: review.timePosted,
        spoiler: 0,
        import_source: "letterboxd",
      };
      this.entryIdx++;
    },

    unselect: function (id) {
      this.confirmedReviews[this.entryIdx] = undefined;
      document.getElementById(`poster${id}`).classList.remove("selected-item");
      document.getElementById(`button${id}`).remove();
    },

    next: function () {
      this.entryIdx++;
    },

    back: function () {
      this.entryIdx -= 1;
    },

    addOption: async function (event) {
      const externalIdResponse = await this.$axios.get("external-id", {
        internalId: event.id,
        medium: this.$mediums.FILM,
      });
      const externalId = externalIdResponse.data.externalId;
      this.clarifications[this.entryIdx].suggestions.push({
        poster_path: event.poster_source.slice(31), //Gets the extension, not whole url
        title: event.title,
        id: externalId,
        release_date: event.year,
      });
      this.confirm(this.clarifications[this.entryIdx], event.id);
    },

    getClasses: function (id) {
      let classes = ["mr-3"];
      if (
        this.entryIdx < this.confirmedReviews.length &&
        this.confirmedReviews[this.entryIdx] &&
        id === this.confirmedReviews[this.entryIdx].workID
      ) {
        classes.push("selected-item");
      }
      return classes;
    },

    submitReviews: function () {
      if (this.contentType === "watched") {
        for (let i = 0; i < this.confirmedReviews.length; i++) {
          if (this.confirmedReviews[i] !== undefined) {
            this.$axios.post("/works_viewed", {
              medium: this.$mediums.FILM,
              internal_id: this.confirmedReviews[i].workID,
            });
          }
        }
      }
      // Add reviews
      else {
        for (let i = 0; i < this.confirmedReviews.length; i++) {
          if (this.confirmedReviews[i] !== undefined) {
            this.$axios.post("/review", this.confirmedReviews[i]);
          }
        }
      }
      //Move to next page (upload complete
      this.entryIdx++;
    },

    reset: function () {
      Object.assign(this.$data, {
        file: undefined,
        contentType: undefined,
        isUploading: false,
        fileUploaded: false,

        clarifications: [],
        entryIdx: 0,

        confirmedReviews: [],

        uploadSuccessful: true,
      });
    },
  },
};
</script>

<style scoped>
#fileInput {
  width: 400px;
}

#contentSelect {
  width: 200px;
  margin-right: 12px;
}

#upload {
  position: relative;
  top: -4px;
}

/* outline selected items */
.v-card.selected-item {
  border: 5px solid rebeccapurple;
}

.v-card__title {
  padding: 4px 8px 8px 8px;
  font-size: 1.4vw;
  word-break: break-word;
}

#searchPrompt {
  margin-top: 17px;
}

.media-search {
  flex-grow: 1;
  margin-right: 20px;
}

h1 {
  font-size: 3.2em;
  padding: 0 40px;
}

.theme--light.v-btn {
  font-size: 1.3em;
}
</style>
